import { useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { GlobeIcon } from 'lucide-react'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { useCountries } from '@/hooks'
import { CountryType } from '@/types/country'

type PropsType = {
  id?: string
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  multiple?: boolean
  disabled?: boolean
  className?: string
  error?: boolean
  valueField?: 'id' | 'iso2'
}

export function CountryCombobox({
  id,
  value,
  onValueChange,
  placeholder,
  multiple = false,
  disabled = false,
  className,
  error: hasError = false,
  valueField = 'iso2'
}: PropsType) {
  const t = useTranslations()
  const { data: countries = [], isLoading, error } = useCountries()

  const options: ComboboxOption[] = useMemo(() => {
    return countries.map((country: CountryType) => ({
      label: `${country.emoji} ${country.name} (${country.iso2})`,
      value: valueField === 'id' ? country.id : country.iso2,
      description: country.capital,
      searchValue: `${country.name} ${country.iso2} ${country.iso3} ${country.capital}`.toLowerCase()
    }))
  }, [countries, valueField])

  if (error) {
    return <div className="text-destructive text-sm">{t('failed_to_load')}</div>
  }

  return (
    <Combobox
      id={id}
      options={options}
      value={value}
      onValueChange={newValue => {
        if (typeof newValue === 'string') {
          onValueChange?.(newValue)
        }
      }}
      placeholder={placeholder || t('select')}
      searchPlaceholder={t('search')}
      emptyText={isLoading ? t('loading') : t('not_found')}
      multiple={multiple}
      disabled={disabled || isLoading}
      icon={<GlobeIcon className="h-4 w-4" />}
      clearable
      error={hasError}
      className={className}
    />
  )
}
